import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between bg-[#0F172A] p-12 lg:flex">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="ESP26" className="h-11 w-11 rounded-xl" />
          <div>
            <p className="font-heading text-xl font-extrabold text-white">ESP26</p>
            <p className="text-xs text-slate-400">People. Skills. Progress.</p>
          </div>
        </div>
        <div className="max-w-md esp-animate">
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white">
            The central operational hub for clinical teams.
          </h1>
          <p className="mt-4 text-slate-400">
            Bring employee records, regions, training, work allocation and development together in one secure place.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <ShieldCheck className="h-4 w-4 text-orange-500" /> Secure role-based access
        </div>
        <div className="pointer-events-none absolute -right-24 top-1/3 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm esp-animate">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src="/logo.png" alt="ESP26" className="h-10 w-10 rounded-xl" />
            <span className="font-heading text-xl font-extrabold">ESP26</span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Welcome back</h2>
          <p className="mt-1 text-sm text-slate-500">Sign in to continue to your workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email address</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@esp26.uk"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-orange-500 py-6 text-base font-semibold hover:bg-orange-600"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
